import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import LoadingScreen from "./components/LoadingScreen";
import { ContentProvider, useContent } from "./context/ContentContext";
import { AuthProvider } from "./context/AuthContext";

import Home from "./pages/Home";
import Destinations from "./pages/Destinations";
import BlogPost from "./pages/BlogPost";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Archive from "./pages/Archive";
import TravelTools from "./pages/TravelTools";
import TravelResources from "./pages/TravelResources";
import NotFound from "./pages/NotFound";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminPosts from "./pages/admin/AdminPosts";
import AdminPostEdit from "./pages/admin/AdminPostEdit";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminAffiliates from "./pages/admin/AdminAffiliates";

function Gated({ children }) {
  const { ready, loading, error } = useContent();
  if (loading) return <LoadingScreen />;
  if (!ready || error) {
    return (
      <div data-testid="content-error" className="min-h-screen flex items-center justify-center p-10 bg-[#FDFBF7]">
        <div className="max-w-md text-center">
          <p className="kicker text-stone-500">Something went sideways</p>
          <h1 className="font-serif text-3xl mt-3 text-stone-900">We couldn't reach the archive.</h1>
          <p className="mt-4 text-stone-600 text-sm">{error || "Please try again in a moment."}</p>
          <button onClick={() => window.location.reload()} className="mt-6 text-sm tracking-[0.2em] uppercase font-semibold text-[#9c4122] link-underline">
            Retry
          </button>
        </div>
      </div>
    );
  }
  return children;
}

function SiteShell() {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin");

  return (
    <>
      {!isAdmin && <Navigation />}
      <Routes>
        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminPosts />} />
          <Route path="posts/new" element={<AdminPostEdit mode="create" />} />
          <Route path="posts/:slug/edit" element={<AdminPostEdit mode="edit" />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="affiliates" element={<AdminAffiliates />} />
        </Route>

        {/* Public routes — gated on content loading */}
        <Route
          path="/*"
          element={
            <Gated>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/destinations" element={<Destinations />} />
                <Route path="/post/:slug" element={<BlogPost />} />
                <Route path="/category/:slug" element={<Archive type="category" />} />
                <Route path="/tag/:slug" element={<Archive type="tag" />} />
                <Route path="/travel-tools" element={<TravelTools />} />
                <Route path="/travel-resources" element={<TravelResources />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                {/* SEO-friendly canonical URL: /<country>/<post-slug> */}
                <Route path="/:countrySlug/:slug" element={<BlogPost />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Gated>
          }
        />
      </Routes>
      {!isAdmin && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App bg-[#FDFBF7] text-stone-900">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ContentProvider>
              <SiteShell />
              <Toaster
                position="bottom-right"
                toastOptions={{
                  classNames: {
                    toast: "bg-white border border-stone-300 text-stone-900 font-sans",
                    description: "text-stone-600",
                  },
                }}
              />
            </ContentProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
